//
// capture.ts — deterministic frame-by-frame render of a Mapbox map
// into a WebM/VP9.
//
// The core loop:
//
//   for each frame i in 0 .. totalFrames-1:
//     view = computeViewAtTime(timeline, initialView, t)
//     map.jumpTo(view)                          // instantaneous seek
//     await map.once('idle')                    // all tiles drawn
//     await requestAnimationFrame               // one compositor flush
//     bitmap = createImageBitmap(canvas)
//     optionally composite HTML overlay + fade layer
//     encoder.encode(new VideoFrame(source))
//
// jumpTo (not flyTo/easeTo) because we render each frame in
// isolation — the animation is reconstructed at playback from the
// timeline, not from a live GL animation. That's what makes output
// smooth regardless of tile-load speed: we don't capture a frame
// until everything for that frame is painted.
//
// Encoding goes through WebCodecs' VideoEncoder configured for VP9
// (profile 0, 8-bit 4:2:0), muxed into WebM via webm-muxer. VP9 was
// chosen over H.264 because Chromium's WebCodecs H.264 encoders
// (both hardware and Chromium-shipped OpenH264 "software") emit a
// low-latency profile with no B-frames and a single reference
// frame regardless of `latencyMode: 'quality'` — visibly soft at
// 4K even at 40 Mbps. Chromium's VP9 encoder is libvpx, which uses
// alt-ref frames and multi-reference prediction and produces
// noticeably sharper output at the same or lower bitrate.
//
// Output bitrates: ~30 Mbit/s at 4K, 12 Mbit/s at 1080p, 6 Mbit/s
// at 720p, 3 Mbit/s below — see pickBitrate. bitrateMode is
// 'variable' so the encoder can redistribute bits toward complex
// frames.
//
// The pipeline assumes preserveDrawingBuffer is true on the WebGL
// context backing map.getCanvas(). That is guaranteed either by
// the mapboxgl.Map monkey-patch or by the HTMLCanvasElement.
// getContext patch in detect.ts.
//
// When options.targetWidth is set, the behaviour is asymmetric:
//
//   - If targetWidth ≤ native canvas width, we leave the map
//     alone and drawImage the captured bitmap at reduced size
//     into an output-sized OffscreenCanvas before encoding. The
//     video frames the same map extent the user sees on screen,
//     just at lower pixel count. Good for previews: the 640-wide
//     preview actually previews the user's intended composition.
//
//   - If targetWidth > native canvas width, we resize the map's
//     WebGL canvas to the target so Mapbox loads more tiles and
//     covers more extent. True high-resolution output with real
//     added detail. Via setPixelRatio (Mapbox ≥ a certain v3.x,
//     no DOM mutation) or via container resize with !important
//     width/height/min-/max- overrides on map.getContainer() as
//     a fallback. The container's full cssText is snapshotted
//     and restored atomically after render so the host app sees
//     no residual changes. Fights against grid parents or JS-
//     driven resize handlers in the host app — for those, a
//     post-resize verification step would be the next defense.
//
import { Muxer, ArrayBufferTarget } from 'webm-muxer';
import { domToCanvas } from 'modern-screenshot';
import type { Map as MapboxMap, LngLatLike } from 'mapbox-gl';
import type { RenderMeta, RenderStartMeta } from '@mve/protocol';
import {
  computeFadeAlpha,
  computeLayerOpacities,
  computeViewAtTime,
  type LayerFade,
  type MapView,
  type OverlayConfig,
  type Timeline,
} from './timeline';
import {
  createLayerFadeController,
  getActiveController,
  getActiveFadeController,
  type LayerFadeController,
} from './layerHide';

//
// Input to render(). The animation is fully described by
// initialView + timeline. onProgress fires once per frame with a
// value in [0, 1]; throwing from it aborts the render (used by the
// panel to cancel when the user closes the result popup).
//
export interface RenderOptions {
  initialView: MapView;
  timeline: Timeline;
  fps: number;
  onProgress?: (fraction: number) => void;
  //
  // Optional HTML overlay capture. When provided with a non-empty
  // `include` list, each captured frame also has a rasterised
  // snapshot of the matching DOM elements composited over the
  // WebGL canvas — useful for Mapbox markers, popups, selected-
  // feature panels, and other app chrome that doesn't live inside
  // the canvas. Undefined or empty `include` means we skip the
  // overlay pipeline entirely (fast path).
  //
  overlayConfig?: OverlayConfig;
  //
  // Mapbox style layer ids to hide for the duration of the render.
  // Used for parts of the visual that live inside the canvas (via
  // style layers) rather than as HTML — e.g. text-field symbol
  // layers the user doesn't want in the output. Visibility is
  // saved at render-start and restored at render-end so the host
  // app sees its layers back the way they were. Unknown ids are
  // skipped silently.
  //
  hiddenLayerIds?: string[];
  //
  // Per-layer opacity fades. Each entry drives a Mapbox style
  // layer's paint-opacity across a time range. See timeline.ts
  // for the data model and computeLayerOpacities for evaluation.
  // Applied per-frame during render; restored to each layer's
  // pre-render opacity on render end.
  //
  layerFades?: LayerFade[];
  //
  // Target output width in pixels. Height is derived from the map
  // canvas's current aspect ratio at render time.
  //
  // Asymmetric handling:
  //
  //   - If targetWidth ≤ the map's current native canvas width, we
  //     leave the map alone and rasterise each captured frame down
  //     to the target via drawImage. The video frames the same
  //     extent as what the user sees on screen — good for previews.
  //   - If targetWidth > the native canvas width, we call
  //     applyTargetResolution which resizes the container (or uses
  //     setPixelRatio when available) so Mapbox renders at the
  //     larger pixel count, loading more tiles and covering more
  //     extent. True added detail, at the cost of a brief
  //     host-layout disruption for the resize.
  //
  // If omitted, the render uses the canvas's native pixel
  // dimensions — no scaling or resizing.
  //
  targetWidth?: number;
  //
  // Composite a watermark text onto every frame (bottom-right
  // corner). Used by the free tier to brand its preview output
  // with our domain. The watermark is drawn into the same
  // OffscreenCanvas the encoder reads from, so it's baked into
  // pixels — removing it from a downloaded video means re-
  // encoding, which is cost the casual user won't bother with.
  //
  watermark?: boolean;
}

//
// Output of render(). The blob is an in-memory MP4 ready to be
// handed to URL.createObjectURL or postMessage.
//
export interface RenderResult {
  blob: Blob;
  meta: RenderMeta;
}

//
// Snapshot the current camera state of a map. Used by the panel
// when the user sets the initial view or uses "Capture current
// view as target" on an action.
//
export function captureView(map: MapboxMap): MapView {
  const c = map.getCenter();
  return {
    center: { lng: c.lng, lat: c.lat },
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
}

//
// Feature detection for the WebCodecs API we need. No fallback
// path exists in the MVP — if this returns false, rendering will
// throw with a human-readable message and the UI disables Render.
//
export function supportsWebCodecs(): boolean {
  return typeof window.VideoEncoder === 'function' && typeof window.VideoFrame === 'function';
}

//
// Compute the render metadata upfront, before any frames are
// produced. Used by the panel to tell the result window "here's
// what's coming" so it can display target dimensions / total frames
// while it waits. Kept in sync with render()'s own dimension / frame-
// count math.
//
export function planRender(
  map: MapboxMap,
  timeline: Timeline,
  fps: number,
  targetWidth?: number,
): RenderStartMeta {
  const canvas = map.getCanvas();
  const aspect = canvas.width / Math.max(1, canvas.height);
  const { width, height } = targetDimensionsFromWidth(targetWidth ?? canvas.width, aspect);
  const totalFrames = Math.max(2, Math.round(timeline.totalSeconds * fps));
  return {
    durationSeconds: timeline.totalSeconds,
    width,
    height,
    fps,
    totalFrames,
  };
}

//
// Render the animation. Drives the map frame-by-frame, encodes each
// rendered canvas into H.264, and muxes the stream into an MP4
// blob. Returns the blob plus its metadata. The caller is
// responsible for restoring the map's camera afterward.
//
export async function render(map: MapboxMap, options: RenderOptions): Promise<RenderResult> {
  if (!supportsWebCodecs()) {
    throw new Error('WebCodecs is not available in this browser. Use a recent Chromium-based browser.');
  }

  const { initialView, timeline, fps, overlayConfig } = options;

  // Decide the output dimensions before touching anything. Based
  // on the map's current canvas aspect; locked in even if we end
  // up resizing the canvas below.
  const canvas = map.getCanvas();
  const nativeW = canvas.width;
  const nativeH = canvas.height;
  const nativeAspect = nativeW / Math.max(1, nativeH);
  const { width, height } = targetDimensionsFromWidth(
    options.targetWidth ?? nativeW,
    nativeAspect,
  );

  // Asymmetric resize strategy: only physically resize the map
  // canvas when the user asked for MORE pixels than the screen is
  // giving us natively. Smaller outputs (previews) are produced by
  // scaling captured frames down during compositing below — the
  // video frames the same extent the user sees on screen, not a
  // cropped subset of it. Only upscale triggers the container-
  // resize / setPixelRatio path, which is where the CSS-fight
  // risk lives.
  const needsUpscale = options.targetWidth !== undefined && options.targetWidth > nativeW;
  const resolutionHandle = needsUpscale
    ? await applyTargetResolution(map, options.targetWidth as number)
    : { restore: () => { /* no-op */ } };

  const totalFrames = Math.max(2, Math.round(timeline.totalSeconds * fps));

  // Single offscreen surface per render. Every frame flows through
  // here: draw the captured bitmap (scaling if needed), then the
  // HTML overlay (if any), then the fade layer (if any), then hand
  // it to the encoder. Consolidates the old "fade canvas" and any
  // ad-hoc compositing into one predictable pipeline.
  const outputCanvas = new OffscreenCanvas(width, height);
  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) throw new Error('Could not acquire a 2D context for compositing.');
  // Use the best available downsampling. Browsers default to 'low'
  // which is visibly crunchy at 6× shrinks.
  outputCtx.imageSmoothingQuality = 'high';

  // Overlay pipeline setup. Skipped entirely when the caller
  // didn't supply include selectors — cheap fast path for the
  // common "no markers" case. Scale is based on the *output*
  // width rather than the canvas width, so the overlay is
  // rasterised at the final output density regardless of whether
  // we're upscaling or downscaling the map pixels.
  const wantsOverlay = !!overlayConfig && overlayConfig.include.length > 0;
  const mapContainer = wantsOverlay ? map.getContainer() : null;
  const overlayScale = mapContainer
    ? width / Math.max(1, mapContainer.offsetWidth)
    : 1;

  // webm-muxer writes chunks into target.buffer as the encoder
  // produces them. We pull the finished buffer out after
  // muxer.finalize().
  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: {
      codec: 'V_VP9',
      width,
      height,
      frameRate: fps,
    },
    firstTimestampBehavior: 'offset',
  });

  // Async error state for the encoder. WebCodecs signals encoder
  // failures through the `error` callback rather than throwing from
  // encode(); by the time encode() rejects it's already too late
  // (the encoder has moved to 'closed' state) and the thrown error
  // is the unhelpful "Cannot call 'encode' on a closed codec". We
  // capture the original error here and re-throw it on the next
  // encode call in the loop so the real failure surfaces.
  let encoderError: Error | null = null;

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (err) => {
      console.error('[mve] VideoEncoder error', err);
      if (!encoderError) {
        encoderError = err instanceof Error ? err : new Error(String(err));
      }
    },
  });

  await configureEncoderWithFallback(encoder, width, height, fps);

  // Hide any layers the user marked as off for this render. Saved
  // state is restored in the finally block below so the live map
  // returns to its pre-render appearance regardless of how the
  // render exits. The returned state also carries a teardown for
  // the 'render' listener that keeps our hide sticky against
  // declarative re-syncs from wrappers like react-map-gl.
  const hiddenLayerState = applyHiddenLayers(map, options.hiddenLayerIds);

  // Per-layer fades: prefer a live controller (so the panel's
  // captured originals carry through without a second read) and
  // fall back to an ephemeral one when running outside the panel.
  // ownsFadeController flags the ephemeral case so we tear it
  // down in the finally block.
  const hasFades = !!options.layerFades && options.layerFades.length > 0;
  let fadeController: LayerFadeController | null = null;
  let ownsFadeController = false;
  if (hasFades) {
    fadeController = getActiveFadeController(map);
    if (!fadeController) {
      fadeController = createLayerFadeController(map);
      ownsFadeController = true;
    }
  }

  try {
    for (let i = 0; i < totalFrames; i++) {
      // Absolute time in seconds. Using i/fps rather than a
      // normalized fraction matches the encoder's frame timestamps
      // and aligns naturally with action.start / action.end.
      const t = i / fps;
      const view = computeViewAtTime(timeline, initialView, t);

      // Apply per-layer fade opacities *before* driving the camera
      // so the subsequent idle wait (inside captureMapFrame) covers
      // both the jumpTo and the setPaintProperty writes — neither
      // is visible until the next paint.
      if (fadeController && options.layerFades) {
        fadeController.setOpacityFactors(
          computeLayerOpacities(options.layerFades, t),
        );
      }

      const bitmap = await captureMapFrame(map, canvas, view);
      const alpha = computeFadeAlpha(timeline, t);

      // HTML overlay snapshot (markers, popups, selected panels,
      // etc.). Built fresh per frame because the overlay DOM
      // mutates as the map moves — markers enter and leave the
      // viewport, labels update, etc.
      const overlayCanvas =
        wantsOverlay && mapContainer && overlayConfig
          ? await captureOverlay(mapContainer, canvas, overlayConfig, overlayScale)
          : null;

      // Unified compositing: draw the map bitmap into the output
      // surface (scaling if bitmap dims differ from output dims),
      // overlay on top, fade on top of that. drawImage is a GPU
      // blit — when bitmap and output are the same size it's ~1:1,
      // and when they differ the browser's compositor handles the
      // scale at HQ interpolation.
      outputCtx.clearRect(0, 0, width, height);
      outputCtx.drawImage(bitmap, 0, 0, width, height);
      if (overlayCanvas) {
        outputCtx.drawImage(overlayCanvas, 0, 0, width, height);
      }
      if (alpha > 0) {
        outputCtx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
        outputCtx.fillRect(0, 0, width, height);
      }
      if (options.watermark) {
        drawWatermark(outputCtx, width, height);
      }

      const timestampUs = Math.round((i / fps) * 1_000_000);
      const frame = new VideoFrame(outputCanvas, {
        timestamp: timestampUs,
        duration: Math.round(1_000_000 / fps),
      });

      // Surface any async encoder failure before we ask it to do
      // more work. Without this check, the next encode() call
      // throws "closed codec" and we lose the original cause.
      if (encoderError) throw encoderError;

      // First frame must be a keyframe; the rest we let the encoder
      // decide.
      encoder.encode(frame, { keyFrame: i === 0 });
      frame.close();
      bitmap.close();

      // Back-pressure. The VideoEncoder accepts frames
      // asynchronously; if we produce faster than it can encode,
      // memory balloons. Draining to ~one-second worth of queued
      // frames keeps us bounded.
      if (encoder.encodeQueueSize > fps * 2) {
        await waitForEncoderDrain(encoder, fps);
      }

      // Throwing from onProgress aborts the render — the panel
      // uses that to cancel when the user closes the result popup.
      options.onProgress?.(i / totalFrames);
    }

    await encoder.flush();
    encoder.close();
    muxer.finalize();
  } catch (err) {
    try { encoder.close(); } catch { /* already closed */ }
    throw err;
  } finally {
    hiddenLayerState.teardown();
    restoreHiddenLayers(map, hiddenLayerState.restore);
    // Ephemeral fade controller: restore opacities we touched and
    // detach. If the panel owns the controller, leave it alone —
    // the panel's scrub-sync effect will re-apply the right
    // opacities for the live scrub position.
    if (ownsFadeController) fadeController?.teardown();
    resolutionHandle.restore();
  }

  options.onProgress?.(1);

  const blob = new Blob([target.buffer], { type: 'video/webm' });
  const meta: RenderMeta = {
    durationSeconds: timeline.totalSeconds,
    width,
    height,
    fps,
    mime: 'video/webm',
  };
  return { blob, meta };
}

//
// Composite the free-tier watermark onto the output canvas. A
// semi-transparent dark pill in the bottom-right corner with our
// domain name on it. Sized proportionally to the output width so
// it stays legible at 720p and doesn't dominate at 4K.
//
// Pixels-only — the encoder reads the canvas after this call, so
// the watermark is baked into the encoded bitstream and can't be
// stripped without a re-encode pass.
//
function drawWatermark(
  ctx: OffscreenCanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const text = 'mapvideoexport.com';
  // Font sized as a fraction of width; clamped so it stays
  // legible even on very narrow outputs and reasonable on 4K.
  const fontPx = Math.max(11, Math.min(28, Math.round(width * 0.012)));
  const padX = Math.round(fontPx * 0.7);
  const padY = Math.round(fontPx * 0.4);
  const margin = Math.max(8, Math.round(fontPx * 0.6));

  ctx.save();
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'top';
  const textWidth = ctx.measureText(text).width;
  const boxW = textWidth + padX * 2;
  const boxH = fontPx + padY * 2;
  const x = width - boxW - margin;
  const y = height - boxH - margin;

  // Pill background.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  const r = boxH / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + boxW - r, y);
  ctx.arc(x + boxW - r, y + r, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + r, y + boxH);
  ctx.arc(x + r, y + r, r, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fillText(text, x + padX, y + padY);
  ctx.restore();
}

//
// Returned by applyTargetResolution; restore() reverses whatever
// strategy was applied so the host map returns to its pre-render
// state. No-op handle is returned when targetWidth is undefined,
// keeping the call site branch-free.
//
interface ResolutionHandle {
  restore: () => void;
}

//
// Drive the map to render at a specific pixel width.
//
// Two strategies, chosen at runtime:
//
//  1. setPixelRatio (Mapbox ≥ a certain v3.x): adjust map pixel
//     ratio in-place. The canvas pixel width becomes `targetWidth`
//     exactly; height follows from the container's clientHeight
//     times the new ratio. No DOM mutation, no CSS fight.
//  2. Container resize (older Mapbox): inline !important width/
//     height/min-/max-/flex overrides on map.getContainer(),
//     derive height from the canvas's current aspect (not the
//     container's — container offsets diverge from canvas aspect
//     when there's padding/borders, which gave us the famous
//     near-square 640×674 output on a widescreen map). Save the
//     container's full inline cssText and restore it atomically
//     so the host page sees no residual changes.
//
// Known-ish failure modes for the container-resize fallback:
//
//   - Host uses `!important` with higher cascade priority than
//     author inline !important: essentially impossible per spec;
//     author inline wins.
//   - Host JS actively re-assigns `style.height` during render
//     (ResizeObserver, effect that reads then writes dimensions):
//     our one-shot write loses. A MutationObserver that re-applies
//     would fix it; not yet implemented.
//   - Parent container is a CSS grid row with a fixed track size:
//     the child (our map container) is forced to the row's height
//     regardless of our inline. Only fix is to tell the host app
//     to give the map a resizeable parent.
//
async function applyTargetResolution(
  map: MapboxMap,
  targetWidth: number | undefined,
): Promise<ResolutionHandle> {
  if (!targetWidth) return { restore: () => { /* nothing to do */ } };

  const container = map.getContainer();
  // Derive target aspect from the *canvas* Mapbox actually renders
  // into, not from container offsets. Container aspects are skewed
  // by padding/border, and that skew showed up as very wrong
  // output dimensions in real-world host apps.
  const canvas0 = map.getCanvas();
  const canvasAspect =
    canvas0.width > 0 && canvas0.height > 0 ? canvas0.width / canvas0.height : 16 / 9;

  const mapAny = map as unknown as {
    setPixelRatio?: (r: number) => void;
    getPixelRatio?: () => number;
  };

  if (typeof mapAny.setPixelRatio === 'function') {
    const saved =
      typeof mapAny.getPixelRatio === 'function'
        ? mapAny.getPixelRatio()
        : window.devicePixelRatio ?? 1;
    // We want the new canvas pixel width to equal targetWidth.
    // Mapbox computes canvas.width = container.clientWidth * ratio,
    // and container.clientWidth stays constant since we're not
    // touching the DOM, so:
    //   new_ratio = targetWidth / (canvas.width / old_ratio)
    //             = targetWidth / container.clientWidth
    // We express it in canvas/saved terms to avoid re-measuring
    // the container in case its CSS is mid-transition.
    const ratio = targetWidth / Math.max(1, canvas0.width / saved);
    mapAny.setPixelRatio(ratio);
    await waitForIdle(map);
    return {
      restore: () => {
        try { mapAny.setPixelRatio?.(saved); } catch { /* noop */ }
      },
    };
  }

  // Container-resize fallback.
  const dpr = window.devicePixelRatio ?? 1;
  const targetHeight = Math.round(targetWidth / canvasAspect);
  const targetCssW = targetWidth / dpr;
  const targetCssH = targetHeight / dpr;
  const savedCssText = container.style.cssText;
  // !important + min/max clamps beat most CSS scenarios we've hit.
  // `flex: 0 0 auto` neutralises parent flex containers that would
  // otherwise stretch us back to the cross-axis size.
  const setImportant = (prop: string, value: string): void => {
    container.style.setProperty(prop, value, 'important');
  };
  setImportant('width', `${targetCssW}px`);
  setImportant('height', `${targetCssH}px`);
  setImportant('min-width', `${targetCssW}px`);
  setImportant('min-height', `${targetCssH}px`);
  setImportant('max-width', `${targetCssW}px`);
  setImportant('max-height', `${targetCssH}px`);
  setImportant('flex', '0 0 auto');
  map.resize();
  await waitForIdle(map);
  return {
    restore: () => {
      // Atomic restore: the saved cssText covers every property we
      // touched (and anything else Mapbox/host might have brushed)
      // in a single assignment, so the host app sees no residual
      // state from the render.
      container.style.cssText = savedCssText;
      try { map.resize(); } catch { /* noop */ }
    },
  };
}

//
// Saved visibility for each layer we hid at render-start. We store
// the resolved value as a plain flag; Mapbox's `visibility` layout
// property is either 'visible' or 'none' in practice (undefined
// resolves to 'visible' by spec default), so a binary is enough.
//
interface HiddenLayerRestore {
  id: string;
  prev: 'visible' | 'none';
}

//
// State returned from installing the hide: the restore info plus a
// teardown for the 'render' event listener we attach below.
//
interface HiddenLayerState {
  restore: HiddenLayerRestore[];
  teardown: () => void;
}

//
// Hide each named layer in the style. A single setLayoutProperty
// call is not enough: react-map-gl's declarative layers (and any
// host-app code that reacts to map events) can re-show a layer
// during render by calling setLayoutProperty or, worse, by
// removing and re-adding the layer — the re-add comes back with
// default 'visible'. A 'render'-event listener can't win that
// race either, because the visible draw has already happened by
// the time the listener fires.
//
// We harden the hide by monkey-patching the mutation entry points
// for the duration of the render:
//
//   - setLayoutProperty: drop any call that tries to re-show one
//     of our hidden layers.
//   - addLayer: splice visibility: 'none' into the spec if a
//     hidden layer is being re-added.
//
// Both original methods are restored in teardown(). Missing layer
// ids and runtime errors are logged so a stale id in a scenario
// doesn't silently fail — everything else is silent.
//
function applyHiddenLayers(
  map: MapboxMap,
  hiddenIds: string[] | undefined,
): HiddenLayerState {
  if (!hiddenIds || hiddenIds.length === 0) {
    return { restore: [], teardown: () => { /* nothing to tear down */ } };
  }

  // If the panel is running a live LayerHideController against this
  // map, the layers are already hidden AND protected by the panel's
  // own monkey-patches. Stacking render's apply + restore on top
  // would be redundant (controller's patches still filter re-shows)
  // and could double the per-frame work, so short-circuit.
  //
  // Correctness note: render and controller share the hiddenLayer
  // Ids value via the panel's render call site, so there's no
  // drift between what the controller hides and what render
  // expects to be hidden.
  if (getActiveController(map)) {
    return { restore: [], teardown: () => { /* nothing to tear down */ } };
  }

  const restore: HiddenLayerRestore[] = [];
  const hiddenSet = new Set<string>();

  for (const id of hiddenIds) {
    try {
      const prev = map.getLayoutProperty(id, 'visibility');
      restore.push({ id, prev: prev === 'none' ? 'none' : 'visible' });
      hiddenSet.add(id);
      map.setLayoutProperty(id, 'visibility', 'none');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(`[mve] ${id}: not found in style (${message})`);
    }
  }

  const mapAny = map as unknown as {
    setLayoutProperty: (layerId: string, name: string, value: unknown, options?: unknown) => unknown;
    addLayer: (layer: unknown, beforeId?: string) => unknown;
  };
  const originalSetLayoutProperty = mapAny.setLayoutProperty;
  const originalAddLayer = mapAny.addLayer;

  mapAny.setLayoutProperty = function patched(
    this: unknown,
    layerId: string,
    name: string,
    value: unknown,
    options?: unknown,
  ): unknown {
    if (name === 'visibility' && hiddenSet.has(layerId) && value !== 'none') {
      return this;
    }
    return originalSetLayoutProperty.call(this, layerId, name, value, options);
  };

  mapAny.addLayer = function patchedAdd(this: unknown, layer: unknown, beforeId?: string): unknown {
    if (layer && typeof layer === 'object' && 'id' in layer) {
      const id = (layer as { id: string }).id;
      if (hiddenSet.has(id)) {
        const layout = (layer as { layout?: Record<string, unknown> }).layout ?? {};
        const patched = { ...(layer as object), layout: { ...layout, visibility: 'none' } };
        return originalAddLayer.call(this, patched, beforeId);
      }
    }
    return originalAddLayer.call(this, layer, beforeId);
  };

  const teardown = (): void => {
    mapAny.setLayoutProperty = originalSetLayoutProperty;
    mapAny.addLayer = originalAddLayer;
  };

  return { restore, teardown };
}

//
// Restore each layer's visibility from the saved info. Wrapped in
// try/catch because the style may have mutated during the render
// (layers added / removed by the host app), in which case there's
// nothing sensible to restore.
//
function restoreHiddenLayers(map: MapboxMap, info: HiddenLayerRestore[]): void {
  for (const { id, prev } of info) {
    try {
      map.setLayoutProperty(id, 'visibility', prev);
    } catch {
      // Layer gone; nothing to do.
    }
  }
}

//
// Rasterise the HTML overlays above the map into a transparent-
// background canvas at the same pixel resolution as the encoded
// frame. Filtering is driven by the user's include / exclude
// selectors: elements (and their subtrees) matching an include
// selector are kept, elements matching an exclude selector are
// stripped — even when nested inside an included element.
//
// The map's own canvas element is always excluded here; we capture
// it separately via createImageBitmap and composite the two.
//
async function captureOverlay(
  container: HTMLElement,
  mapCanvas: HTMLCanvasElement,
  config: OverlayConfig,
  scale: number,
): Promise<HTMLCanvasElement> {
  const keep = buildKeepSet(container, config, mapCanvas);
  const drop = buildDropSet(container, config);

  return await domToCanvas(container, {
    backgroundColor: null,
    scale,
    filter: (node: Node): boolean => {
      // Non-Element nodes (text, comments) inherit visibility from
      // their parent, so let them through.
      if (!(node instanceof Element)) return true;
      if (node === mapCanvas) return false;
      if (drop.has(node)) return false;
      return keep.has(node);
    },
  });
}

//
// Pre-compute the set of elements to keep. For each include
// selector we add the matching elements, every ancestor up to the
// root (so modern-screenshot can descend into them), and every
// descendant (so the included subtree is rendered in full).
// Building the set up front turns each per-node filter call into
// an O(1) Set lookup.
//
function buildKeepSet(
  root: HTMLElement,
  config: OverlayConfig,
  mapCanvas: HTMLCanvasElement,
): Set<Element> {
  const keep = new Set<Element>();
  // The root must always be kept so the traversal can begin.
  keep.add(root);
  for (const selector of config.include) {
    let matches: NodeListOf<Element>;
    try {
      matches = root.querySelectorAll(selector);
    } catch {
      // Invalid selector — skip silently so one typo doesn't abort
      // the whole render.
      continue;
    }
    for (const el of matches) {
      if (el === mapCanvas) continue;
      keep.add(el);
      // Ancestor chain up to (but not past) the root.
      let p: Element | null = el.parentElement;
      while (p && p !== root.parentElement) {
        keep.add(p);
        p = p.parentElement;
      }
      // Full descendant subtree.
      for (const d of el.querySelectorAll('*')) keep.add(d);
    }
  }
  return keep;
}

//
// Pre-compute the set of elements to drop. Each exclude selector
// contributes its matches and their descendants. Used by the
// filter callback as a fast lookup to short-circuit traversal
// below an excluded node.
//
function buildDropSet(root: HTMLElement, config: OverlayConfig): Set<Element> {
  const drop = new Set<Element>();
  for (const selector of config.exclude) {
    let matches: NodeListOf<Element>;
    try {
      matches = root.querySelectorAll(selector);
    } catch {
      continue;
    }
    for (const el of matches) {
      drop.add(el);
      for (const d of el.querySelectorAll('*')) drop.add(d);
    }
  }
  return drop;
}

//
// Maximum number of retries when a tile (or other source) errors
// during frame capture. Each retry waits a moment for any in-
// flight retry at the Mapbox level to land, triggers a repaint to
// re-request missing tiles, then waits for idle again. Most real
// transient failures clear on the first retry; after three we give
// up and capture whatever we have rather than stalling the render.
//
const CAPTURE_ERROR_RETRIES = 3;
const CAPTURE_ERROR_BACKOFF_MS = 250;

//
// Drive the map to a target camera state and capture one frame's
// worth of pixels. Wraps the jumpTo → idle → rAF → createImageBitmap
// sequence with a retry loop that reacts to Mapbox 'error' events
// (typically tile load failures). Without this retry, a transient
// tile error would survive into the encoded video as a black
// triangle, because Mapbox's 'idle' event fires as soon as tile
// requests complete — including when they complete with an error.
//
async function captureMapFrame(
  map: MapboxMap,
  canvas: HTMLCanvasElement,
  view: MapView,
): Promise<ImageBitmap> {
  let errored = false;
  const onError = (): void => {
    errored = true;
  };
  map.on('error', onError);

  try {
    map.jumpTo({
      center: [view.center.lng, view.center.lat] as LngLatLike,
      zoom: view.zoom,
      bearing: view.bearing,
      pitch: view.pitch,
    });

    // Don't capture until the map has finished loading tiles and
    // drawing for the new camera state. This is the key to the
    // "smooth regardless of network" property.
    await waitForIdle(map);
    // One rAF forces the compositor to paint what Mapbox just
    // drew. Without it, the first readback occasionally returns
    // a half-composited frame.
    await nextAnimationFrame();

    let attempt = 0;
    while (errored && attempt < CAPTURE_ERROR_RETRIES) {
      attempt++;
      errored = false;
      console.warn(`[mve] tile error during capture — retry ${attempt}/${CAPTURE_ERROR_RETRIES}`);
      // Brief pause: gives any in-flight Mapbox-level tile retry
      // room to land before we trigger another load cycle.
      await new Promise((resolve) => setTimeout(resolve, CAPTURE_ERROR_BACKOFF_MS));
      // triggerRepaint walks the tile pyramid for the current view
      // and re-issues requests for any tiles the cache doesn't
      // currently hold. If the previous error was transient, the
      // retry fires a fresh request that succeeds.
      map.triggerRepaint();
      await waitForIdle(map);
      await nextAnimationFrame();
    }

    return await createImageBitmap(canvas);
  } finally {
    map.off('error', onError);
  }
}

//
// Resolve once Mapbox fires its 'idle' event — meaning all tiles
// are loaded, all sources are settled, no in-flight animations,
// and the most recent frame has finished drawing. This is exactly
// the point at which the canvas has the pixels we want.
//
function waitForIdle(map: MapboxMap): Promise<void> {
  return new Promise((resolve) => map.once('idle', () => resolve()));
}

//
// Resolve on the next animation frame. Used after jumpTo to let
// the compositor finish presenting Mapbox's output before we try
// to read the canvas.
//
function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

//
// Poll until the VideoEncoder's internal queue has drained to a
// manageable size. Prevents unbounded memory growth when frame
// production outruns encoding.
//
function waitForEncoderDrain(encoder: VideoEncoder, fps: number): Promise<void> {
  return new Promise((resolve) => {
    const tick = (): void => {
      if (encoder.encodeQueueSize <= fps) resolve();
      else setTimeout(tick, 10);
    };
    tick();
  });
}

//
// Compute output dimensions for a desired width + a target aspect
// ratio. Both returned numbers are even (H.264 and most other
// codecs reject odd dimensions at configure() time). Used by
// planRender for the pre-flight metadata and by render() itself
// to decide the output canvas size.
//
function targetDimensionsFromWidth(
  targetWidth: number,
  aspect: number,
): { width: number; height: number } {
  const width = Math.max(2, Math.round(targetWidth) - (Math.round(targetWidth) % 2));
  const heightRaw = Math.round(targetWidth / Math.max(0.0001, aspect));
  const height = Math.max(2, heightRaw - (heightRaw % 2));
  return { width, height };
}

//
// Pick a VP9 codec string for the output resolution. The four
// fields are profile.level.bit-depth.chroma-subsampling:
//
//   00 = profile 0 (8-bit 4:2:0)
//   LL = level (higher = more pixels/second allowed)
//   08 = 8-bit depth
//   00 = 4:2:0 chroma subsampling (YUV420)
//
// Levels: 3.0 tops out around 1280×720@30, 4.1 around 1920×1080@30,
// 5.1 around 3840×2160@30. We pick one step above the minimum
// required so the encoder has headroom and so mildly-odd aspect
// ratios (non-16:9 4K outputs from pitched/rotated maps) don't
// overshoot the level's pixel-rate budget.
//
function selectVp9Codec(width: number, height: number): string {
  const pixels = width * height;
  if (pixels > 1920 * 1080) return 'vp09.00.51.08'; // 4K and above
  if (pixels > 1280 * 720) return 'vp09.00.41.08';  // 1080p
  return 'vp09.00.30.08';                             // 720p and below
}

//
// Configure the VideoEncoder for VP9 at the given dimensions.
// Validates via isConfigSupported and falls back to a higher level
// if the natural level rejects the frame geometry (e.g. weirdly-
// shaped 4K outputs with more pixels than Level 5.1 allows). A
// clear error is raised if no candidate is supported so the user
// doesn't see the secondary "closed codec" failure that Chromium
// raises when configure() quietly rejects.
//
async function configureEncoderWithFallback(
  encoder: VideoEncoder,
  width: number,
  height: number,
  fps: number,
): Promise<void> {
  const bitrate = pickBitrate(width, height);
  const primary = selectVp9Codec(width, height);
  // Level 6.2 — the top of VP9's level table. Handles anything up
  // to 8K. Used as a safety net for dimensions that overflow the
  // primary level's macroblock budget.
  const LEVEL_62 = 'vp09.00.62.08';
  const codecCandidates = primary === LEVEL_62 ? [LEVEL_62] : [primary, LEVEL_62];

  for (const codec of codecCandidates) {
    const config: VideoEncoderConfig = {
      codec,
      width,
      height,
      bitrate,
      bitrateMode: 'variable',
      framerate: fps,
      latencyMode: 'quality',
    };
    try {
      const support = await VideoEncoder.isConfigSupported(config);
      if (support.supported && support.config) {
        encoder.configure(support.config);
        console.log(
          `[mve] VideoEncoder configured: codec=${support.config.codec ?? codec} ` +
            `${width}×${height}@${fps} bitrate=${bitrate}`,
        );
        return;
      }
    } catch {
      // isConfigSupported can throw on malformed configs. Swallow
      // and try the next candidate.
    }
  }

  throw new Error(
    `VideoEncoder can't encode ${width}×${height} @ ${fps} fps on this device. ` +
      'Try a lower resolution preset.',
  );
}

//
// Heuristic VP9 bitrate by resolution. VP9 delivers similar
// subjective quality at roughly half the H.264 bitrate thanks to
// alt-ref frames, multi-reference prediction, and better
// transforms. These numbers target YouTube-tier quality ("high
// 4K" guidance is ~35 Mbps VP9) with some headroom for
// terrain-heavy scenes. bitrateMode: 'variable' lets the encoder
// redistribute bits toward complex frames.
//
function pickBitrate(width: number, height: number): number {
  const pixels = width * height;
  if (pixels > 1920 * 1080) return 40_000_000; // ≈ 4K and above
  if (pixels > 1280 * 720) return 12_000_000;  // ≈ 1080p
  if (pixels > 640 * 360) return 6_000_000;    // ≈ 720p
  return 3_000_000;                             // ≤ 540p (preview tier)
}
